import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { Loader2, TrendingUp, Users, Tag, Sparkles } from "lucide-react";
import { getAnalytics } from "@/lib/content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const PRESETS = [
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "90", label: "Last 90 days", days: 90 },
  { key: "all", label: "All time", days: null },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white hairline shadow-soft p-5">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${accent || "bg-orange-50"}`}>
          <Icon className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">{label}</div>
          <div className="font-display font-black text-2xl text-ink">{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [preset, setPreset] = useState("30");
  const [start, setStart] = useState(daysAgoISO(30));
  const [end, setEnd] = useState(todayISO());

  const load = async (params) => {
    setLoading(true); setErr(null);
    try {
      const d = await getAnalytics(params);
      setData(d);
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/login", { replace: true }); return; }
      setErr(e.message || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load({ start: start || undefined, end: end || undefined });
    // eslint-disable-next-line
  }, []);

  const applyPreset = (p) => {
    setPreset(p.key);
    if (p.days == null) {
      setStart(""); setEnd("");
      load({});
    } else {
      const s = daysAgoISO(p.days);
      const e = todayISO();
      setStart(s); setEnd(e);
      load({ start: s, end: e });
    }
  };

  const byDay = data?.by_day || [];
  const bySource = data?.by_source || [];
  const byProject = data?.by_project_type || [];

  const total = data?.total_leads ?? 0;
  const avg = data?.avg_estimate ?? 0;
  const topSource = bySource[0]?.source || "—";
  const topProject = byProject[0]?.project_type || "—";

  // chart: ensure data is array with date + count
  const chartData = useMemo(() => {
    if (!byDay.length) return [];
    // label formatting
    return byDay.map((d) => ({
      date: d.date.slice(5), // MM-DD
      leads: d.count,
    }));
  }, [byDay]);

  const maxSource = Math.max(1, ...bySource.map((s) => s.count));

  return (
    <div data-testid="admin-analytics">
      <div>
        <h2 className="font-display font-bold text-2xl text-ink">Analytics</h2>
        <p className="text-sm text-muted-ink mt-1">
          Lead flow, top sources and project mix.
        </p>
      </div>

      {/* Filters */}
      <div className="mt-4 rounded-2xl bg-white hairline shadow-soft p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p)}
              data-testid={`analytics-preset-${p.key}`}
              className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                preset === p.key
                  ? "bg-ink text-white"
                  : "bg-white hairline text-ink hover:bg-orange-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-xs font-bold text-ink">Start</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                   className="mt-1.5 h-11 rounded-xl" data-testid="analytics-start-date" />
          </div>
          <div>
            <Label className="text-xs font-bold text-ink">End</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                   className="mt-1.5 h-11 rounded-xl" data-testid="analytics-end-date" />
          </div>
          <Button
            onClick={() => { setPreset("custom"); load({ start: start || undefined, end: end || undefined }); }}
            className="h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
            data-testid="analytics-apply"
          >
            Apply
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 grid place-items-center p-20"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
      ) : err ? (
        <div className="mt-8 p-10 text-center text-red-600 font-semibold">{err}</div>
      ) : (
        <>
          {/* Stats */}
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Users} label="Total leads" value={total} />
            <Stat icon={TrendingUp} label="Avg estimate" value={avg ? `₹${new Intl.NumberFormat("en-IN").format(avg)}` : "—"} />
            <Stat icon={Tag} label="Top source" value={topSource} />
            <Stat icon={Sparkles} label="Top project" value={topProject} />
          </div>

          {/* Leads/day chart */}
          <div className="mt-5 rounded-2xl bg-white hairline shadow-soft p-5 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">Leads per day</div>
                <div className="font-display font-bold text-lg text-ink">Trend</div>
              </div>
            </div>
            <div className="mt-4 h-72 min-h-[280px]" data-testid="analytics-chart">
              {chartData.length === 0 ? (
                <div className="h-full grid place-items-center text-muted-ink text-sm">No data in this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={280}>
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                    <defs>
                      <linearGradient id="bppArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F97316" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
                    <Area type="monotone" dataKey="leads" stroke="#F97316" strokeWidth={2.5} fill="url(#bppArea)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Source + project breakdown */}
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white hairline shadow-soft p-5 md:p-6">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">Sources</div>
              <div className="font-display font-bold text-lg text-ink mt-0.5">Where leads come from</div>
              <div className="mt-4 space-y-3" data-testid="analytics-sources">
                {bySource.length === 0 && (
                  <div className="text-sm text-muted-ink">No data.</div>
                )}
                {bySource.map((s) => (
                  <div key={s.source}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-bold text-ink">{s.source}</span>
                      <span className="text-muted-ink">{s.count}</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: `${(s.count / maxSource) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white hairline shadow-soft p-5 md:p-6">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-muted-ink">Project mix</div>
              <div className="font-display font-bold text-lg text-ink mt-0.5">By project type</div>
              <div className="mt-4 h-56 min-h-[220px]" data-testid="analytics-project-mix">
                {byProject.length === 0 ? (
                  <div className="h-full grid place-items-center text-muted-ink text-sm">No data.</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                    <BarChart data={byProject} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="project_type" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-10} dy={5} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {byProject.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? "#F97316" : "#FDBA74"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
